import {
	defaultLogCardCopy,
	type LogCardCopy,
} from "@llmgateway/shared/components";

import type { Locale } from "@llmgateway/i18n";

const zhCNLogCardCopy = {
	...defaultLogCardCopy,
	copied: "已复制",
	imageGenerated: "图片已生成",
	imageGeneratedViewDetails: "图片已生成 - 查看详情",
	inputImageTitle: "输入图片",
	generatedImageTitle: "生成图片",
	dateFormat: "yyyy年M月d日 HH:mm:ss",
	toolCalls: (names: string) => `工具调用：${names}`,
	toolCallsExecuted: "工具调用已执行",
	finishReason: (reason: string | null | undefined) => {
		if (!reason) {
			return "-";
		}
		const labels: Record<string, string> = {
			completed: "已完成",
			content_filter: "内容过滤",
			error: "错误",
			length: "长度限制",
			stop: "停止",
			tool_calls: "工具调用",
		};
		return labels[reason] ?? reason;
	},
	retentionTooltip: "在组织策略中开启保留后，才会存储响应内容",
	retried: "已重试",
	viewDetails: "查看详情",
	toggleDetails: "展开或收起详情",
	cacheStatus: {
		fully: "完全命中缓存",
		partial: "部分命中缓存",
		none: "未命中缓存",
	},
	cachedTokens: (tokens: string | number) => `（${tokens} 已缓存）`,
	tokenCount: (tokens: string | number | null | undefined) =>
		`${tokens} tokens`,
	providerCost: "供应商成本",
	notDeductedFromYourBalance: " - 不会从你的余额中扣除",
	notDeductedFromBalance: " - 不会从余额中扣除",
	discountOff: (percent: string) => `${percent}% 折扣`,
	pluginCount: (count: number) => `${count} 个插件`,
	requestDetails: "请求详情",
	projectId: "项目 ID",
	apiKey: "API 密钥",
	requestedModel: "请求模型",
	usedModel: "实际模型",
	usedModelProviderMapping: "实际模型供应商映射",
	provider: "供应商",
	routingInfo: "路由信息",
	selection: "选择原因",
	key: "密钥",
	set: "已设置",
	unset: "未设置",
	active: "生效",
	available: "可用项",
	scores: "评分",
	rpmCapped: "RPM 受限",
	contentFilter: "内容过滤",
	cache: "缓存",
	requestAttempts: "请求尝试",
	log: "日志",
	ok: "正常",
	responseMetrics: "响应指标",
	duration: "耗时",
	throughput: "吞吐",
	timeToFirstToken: "首 token 时间",
	timeToFirstReasoningToken: "首推理 token 时间",
	responseSize: "响应大小",
	unknown: "未知",
	bytes: "字节",
	promptTokens: "输入 Tokens",
	completionTokens: "输出 Tokens",
	totalTokens: "总 Tokens",
	cachedInputTokens: "缓存输入 Tokens",
	cacheWriteTokens: "缓存写入 Tokens",
	reasoningTokens: "推理 Tokens",
	imageInputTokens: "图片输入 Tokens",
	imageOutputTokens: "图片输出 Tokens",
	audioInputTokens: "音频输入 Tokens",
	originalFinishReason: "原始结束原因",
	unifiedFinishReason: "统一结束原因",
	streamed: "流式",
	canceled: "已取消",
	cached: "缓存",
	yes: "是",
	no: "否",
	costInformation: "成本信息",
	providerPricing: "供应商计费",
	inputCost: "输入成本",
	outputCost: "输出成本",
	cachedInputCost: "缓存输入成本",
	cacheWriteCost: "缓存写入成本",
	requestCost: "请求成本",
	nativeWebSearchCost: "原生网页搜索成本",
	contentFilterCost: "内容过滤成本",
	imageInputCost: "图片输入成本",
	imageOutputCost: "图片输出成本",
	videoOutputCost: "视频输出成本",
	audioInputCost: "音频输入成本",
	inferenceTotal: "推理合计",
	discountApplied: "已应用折扣",
	pricingTier: "价格档位",
	serviceTier: "服务档位",
	billedToYourOrganization: "向你的组织计费",
	billedToOrganization: "向组织计费",
	dataStorage: "数据存储",
	metadata: "元数据",
	date: "日期",
	requestId: "请求 ID",
	traceId: "Trace ID",
	sessionId: "会话 ID",
	logId: "日志 ID",
	source: "来源",
	userAgent: "User Agent",
	project: "项目",
	organization: "组织",
	mode: "模式",
	usedMode: "实际模式",
	copyRequestId: "复制请求 ID",
	copyTraceId: "复制 Trace ID",
	copySessionId: "复制会话 ID",
	copyLogId: "复制日志 ID",
	copyProjectId: "复制项目 ID",
	copyOrganizationId: "复制组织 ID",
	copyApiKeyId: "复制 API 密钥 ID",
	customHeaders: "自定义请求头",
	modelParameters: "模型参数",
	temperature: "Temperature",
	temperatureDescription: "控制随机性：数值越高，输出越随机",
	maxTokens: "最大 Tokens",
	maxTokensDescription: "最多生成的 token 数量",
	topP: "Top P",
	topPDescription: "temperature 的替代参数，通过 nucleus sampling 控制多样性",
	frequencyPenalty: "频率惩罚",
	frequencyPenaltyDescription: "降低重复同一 token 的概率",
	reasoningEffort: "推理强度",
	reasoningEffortDescription: "为支持推理的模型指定 chain-of-thought 强度",
	reasoningBudget: "推理预算",
	reasoningBudgetDescription:
		"为推理分配的精确 token 预算（reasoning config 中的 max_tokens）",
	effort: "Effort",
	effortDescription: "控制受支持模型的计算强度",
	responseFormat: "响应格式",
	responseFormatDescription:
		"请求的输出格式（text、json_object 或 json_schema）",
	aspectRatio: "宽高比",
	imageSize: "图片尺寸",
	imageQuality: "图片质量",
	imageCount: "图片数量",
	outputFormat: "输出格式",
	compression: "压缩",
	seed: "种子",
	plugins: "插件",
	pluginResults: "插件结果",
	responseHealing: "响应修复：",
	applied: "已应用",
	notNeededValidJson: "无需修复（有效 JSON）",
	additionalParameters: "其他参数",
	toolInformation: "工具信息",
	availableTools: "可用工具",
	toolChoice: "工具选择",
	toolCallsTitle: "工具调用",
	unknownFunction: "未知函数",
	idLabel: "ID",
	notAvailable: "不可用",
	arguments: "参数：",
	builtinTools: "内置工具",
	nativeWebSearch: "原生网页搜索",
	webSearchUsed: "本次请求使用了网页搜索",
	cost: "成本",
	errorDetails: "错误详情",
	statusCode: "状态码",
	statusText: "状态文本",
	errorMessage: "错误消息",
	requestRetriedSucceeded: "该请求已重试并成功。",
	viewSuccessfulRequest: "查看成功请求",
	messageContext: "消息上下文",
	previewInput: "预览输入",
	base64ImageData: "[base64 图片数据]",
	messageDataNotRetained:
		"消息数据未保留。请在组织策略中开启保留，以存储请求消息。",
	noMessageData: "没有可用的消息数据。",
	reasoningContent: "推理内容",
	response: "响应",
	previewImage: "预览图片",
	imageGeneratedSentence: "图片已生成。",
	viewImage: "查看图片",
	responseContentNotRetained:
		"响应内容未保留。请在组织策略中开启保留，以存储响应数据。",
	noResponseContent: "没有可用的响应内容。",
	previewOfImages: (title: string, count: number) =>
		`本次请求中的${title}预览${count > 1 ? `（${count} 张）` : ""}。`,
	imageAlt: (index: number) => `生成图片 ${index}`,
} satisfies LogCardCopy;

export function getLogCardCopy(locale: Locale): LogCardCopy {
	return locale === "zh-CN" ? zhCNLogCardCopy : defaultLogCardCopy;
}
